import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { getMonthAppointments, getAccountingEntries, getAllEmployees, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import jsPDF from 'jspdf';
import { signOutUser } from '../services/firebase';
import { 
  FiLogOut, 
  FiUser, 
  FiMenu,
  FiX,
  FiGlobe,
  FiPlus,
  FiDatabase,
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiSettings,
  FiFileText,
  FiMessageSquare
} from 'react-icons/fi';
import Sidebar from './Sidebar';
import LogoImage from './LogoImage';
import { useLang } from '../i18n/lang';
import BusinessNotifications from './BusinessNotifications';
// (Eliminado: activar licencia / estado de plan)

const Navbar = () => {
  const { user, userClaims, isAdmin, isGlobalAdmin, isBusinessAdmin, isClient, tenantId } = useSimpleAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Notificaciones movidas al interior de la pantalla de soporte; no mostrar badge en Navbar
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Por defecto abierto en escritorio
  const [userDetails, setUserDetails] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ displayName: '', phone: '' });
  const { t } = useLang();

  // Usar solo los datos del usuario de los claims (sin lectura directa de Firestore)
  useEffect(() => {
    if (user && userClaims) {
      setUserDetails({
        displayName: userClaims.name || user.displayName,
        email: user.email,
        role: userClaims.role,
        tenantId: userClaims.tenantId,
        approved: userClaims.approved
      });
    } else {
      setUserDetails(null);
    }
  }, [user, userClaims]);

  // Ampliar con teléfono desde perfil en Firestore
  useEffect(() => {
    (async () => {
      try {
        if (!user?.uid) return;
        // Esperar a que existan claims/tenantId para evitar lecturas sin permisos
        if (!userClaims?.tenantId) return;
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setUserDetails((prev) => ({ ...(prev || {}), phone: data.phone || '' }));
        }
      } catch (_) {}
    })();
  }, [user?.uid, userClaims?.tenantId]);

  // Cerrar sidebar automáticamente en móvil
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) { // lg breakpoint
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Ejecutar al cargar

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generación automática de informe mensual (último día del mes)
  React.useEffect(() => {
    const run = async () => {
      try {
        if (!user || !isAdmin) return;
        const auto = false; // Simplificado: no auto-reportes
        if (!auto) return;
        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        if (now.getDate() !== lastDay) return;
        const key = `monthly_report_${now.getFullYear()}_${now.getMonth()+1}`;
        if (localStorage.getItem(key) === 'done') return;

        // Datos
        const [apts, employees, accounting] = await Promise.all([
          getMonthAppointments(now.getFullYear(), now.getMonth()),
          getAllEmployees(),
          getAccountingEntries()
        ]);

        // Agrupar por empleado
        const empMap = {};
        employees.forEach(e => { empMap[e.id] = e.displayName || e.name || e.id; });
        const byEmp = {};
        (apts || []).forEach(a => {
          const emp = a.employeeId || 'sin';
          if (!byEmp[emp]) byEmp[emp] = [];
          byEmp[emp].push(a);
        });

        // Sumar ingresos/gastos del mes
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        const inMonth = (d) => {
          try {
            const dt = d.date?.toDate ? d.date.toDate() : new Date(d.date);
            return dt >= start && dt <= end;
          } catch { return false; }
        };
        const entries = (accounting || []).filter(inMonth);
        const expense = entries.filter(e => e.type === 'expense').reduce((s, e) => s + (e.amount||0), 0);
        // Ingresos desde citas (suma de subtotales por empleado)
        const income = Object.keys(byEmp).reduce((sum, empId) => {
          const empTotal = (byEmp[empId] || []).reduce((s, a) => s + (a.price || 0), 0);
          return sum + empTotal;
        }, 0);

        // Crear PDF
        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text("Reserberry - Informe mensual", 14, 18);
        doc.setFontSize(11);
        doc.text(`Mes: ${now.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`, 14, 26);
        doc.text(`Ingresos: €${income.toFixed(2)}   Gastos: €${expense.toFixed(2)}   Balance: €${(income-expense).toFixed(2)}`, 14, 34);

        let y = 44;
        Object.keys(byEmp).forEach((empId) => {
          const nombre = empMap[empId] || 'Sin asignar';
          doc.setFontSize(12);
          doc.text(`Empleado: ${nombre} (${byEmp[empId].length} citas)`, 14, y);
          y += 6;
          doc.setFontSize(10);
          const empList = byEmp[empId]
            .sort((a,b) => {
              const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date||0);
              const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date||0);
              return ad - bd;
            });
          empList.forEach(a => {
              if (y > 280) { doc.addPage(); y = 20; }
              const d = a.date?.toDate ? a.date.toDate() : new Date(a.date);
              const line = `${d.toLocaleDateString('es-ES')} ${a.time||''} · ${a.type||'Servicio'} · €${(a.price||0).toFixed(2)} · ${a.userName||a.userEmail||a.userId||''}`;
              doc.text(line, 16, y);
              y += 6;
            });
          // Subtotal por empleado
          const empTotal = empList.reduce((s, a) => s + (a.price || 0), 0);
          if (y > 280) { doc.addPage(); y = 20; }
          doc.setFontSize(10);
          doc.text(`Subtotal ${nombre}: ${empList.length} citas • €${empTotal.toFixed(2)}`, 16, y);
          y += 8;
          y += 4;
        });

        const file = `informe-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}.pdf`;
        doc.save(file);
        localStorage.setItem(key, 'done');
      } catch (e) {
        // Silencioso para no molestar al admin si falla
        console.warn('No se pudo generar informe mensual:', e);
      }
    };
    run();
  }, [user, isAdmin]);

  // (Eliminado: carga de estado de plan/licencia)

  const handleSignOut = async () => {
    const confirmed = window.confirm('¿Seguro que quieres cerrar sesión?');
    if (!confirmed) return;
    try {
      await signOutUser();
      // Forzar navegación con replace para evitar problemas de caché en móviles
      navigate('/login', { replace: true });
      // Forzar recarga de la página para limpiar completamente el estado
      // Agregar timestamp para evitar caché
      const timestamp = new Date().getTime();
      window.location.href = `/login?t=${timestamp}`;
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // (Eliminado: modo oscuro)

  // No renderizar navbar en la página de login
  if (location && location.pathname === '/login') {
    return null;
  }

  // Mostrar una barra mínima y neutral en la ruta de registro de negocio
  const isBusinessRegistrationRoute = location && (location.pathname === '/register-business' || location.pathname === '/admin-register');
  if (isBusinessRegistrationRoute) {
    return (
      <nav className="bg-white shadow-lg border-b border-gray-200" style={{ minHeight: '104px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32">
            {/* Logo fijo en escritorio */}
            <div className="hidden lg:block mr-4">
              <Link 
                to={isGlobalAdmin ? '/global-admin' : (isBusinessAdmin ? '/admin' : (isClient ? '/booking' : '/'))}
                className="flex items-center"
              >
                <LogoImage className="h-10 w-10 object-contain" />
              </Link>
            </div>
            <div className="flex items-center flex-shrink-0 min-w-0 max-w-xs">
              <span className="ml-2 text-lg sm:text-xl font-bold text-gray-900">Reserberry</span>
            </div>
            {/* Sin acciones de sesión en la página de registro de negocio */}
            <div className="flex items-center space-x-2 flex-shrink-0" />
          </div>
        </div>
      </nav>
    );
  }

  if (!user) return null;

  return (
    <>
      {/* Sidebar solo en móvil/tablet */}
      <div className="lg:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      </div>
      
      {/* Navbar principal */}
      <nav className="bg-white shadow-lg border-b border-gray-200" style={{ minHeight: '104px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo SIEMPRE visible (móvil y escritorio) */}
            <div className="flex items-center flex-shrink-0 mr-3">
              <Link 
                to={isGlobalAdmin ? '/global-admin' : (isBusinessAdmin ? '/admin' : (isClient ? '/booking' : '/'))}
                className="flex items-center"
                onClick={closeMobileMenu}
              >
                <LogoImage className="h-24 w-auto object-contain" />
              </Link>
            </div>
 
            {/* Navegación horizontal en escritorio */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Logo ya renderizado a la izquierda; mantener separación */}
              <span className="mr-1" />
              {isGlobalAdmin ? (
                <>
                  {/* Botones superiores innecesarios eliminados para GlobalAdmin */}
                </>
              ) : isBusinessAdmin ? (
                <>
                  <Link to="/admin/day" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/day') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.dashboard')}</Link>
                  <Link to="/admin/appointments" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/appointments') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.appointments')}</Link>
                  <Link to="/admin/accounting" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/accounting') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.accounting')}</Link>
                  <Link to="/admin/users" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/users') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.users')}</Link>
              {/* Inventario eliminado */}
                  <Link to="/admin/services" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/services') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.services')}</Link>
                  <Link to="/admin/employees" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/employees') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.employees')}</Link>
                  <Link to="/admin/cuadrante" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/cuadrante') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.cuadrante')}</Link>
                  <Link to="/admin/reports" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/reports') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.reports')}</Link>
                  <Link to="/admin/date-control" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/date-control') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.dateControl')}</Link>
                  <Link to="/admin/config" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/admin/config') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>{t('nav.config')}</Link>
                  <Link to="/support" className={`px-3 py-2 rounded-md text-sm font-medium ${location.pathname.startsWith('/support') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                    {t('nav.support')}
                  </Link>
                </>
              ) : null}
            </div>
 
            {/* Botones de sesión y menú (compactos) */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Campanita de notificaciones para admins de negocio */}
            {isBusinessAdmin && !isGlobalAdmin && (
              <BusinessNotifications />
            )}
            {/* Info de cuenta en escritorio (top-right) */}
            <div className="hidden lg:flex items-center text-sm text-gray-700 mr-2 max-w-xs">
              <FiUser className="mr-2" />
              <div className="flex flex-col items-end text-right">
                <span className="font-medium truncate max-w-[220px]">
                  {userDetails?.displayName || user.displayName || user.email}
                </span>
                <span className="text-xs text-gray-500 truncate max-w-[220px]">
                  {user.email}
                </span>
              </div>
            </div>
              <button
                onClick={handleSignOut}
                className="p-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-gray-100 transition-colors"
                title={t('common.signOut')}
              >
                <FiLogOut className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
                aria-label={t('common.openMenu') || 'Abrir menú'}
              >
                {isMobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
              </button>
            </div>
          </div>
 
          {/* Menú móvil desplegable */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 bg-white">
              {/* Información del usuario en móvil */}
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-700 flex flex-col items-end text-right">
                  <div className="flex items-center justify-end">
                    <FiUser className="mr-2" />
                    <span className="font-medium">{userDetails?.displayName || user.displayName || user.email}</span>
                    {isGlobalAdmin && (
                      <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Super Admin</span>
                    )}
                    {isBusinessAdmin && (
                      <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">Admin</span>
                    )}
                  </div>
                  <div className="mt-1 text-gray-600 break-all">
                    {user.email}
                  </div>
                  {userDetails?.phone && (
                    <div className="text-gray-600">{userDetails.phone}</div>
                  )}
                </div>
              </div>
 
              {/* Enlaces de navegación móvil */}
              <div className="px-2 pt-2 pb-3 space-y-1">
                {isGlobalAdmin ? (
                  <>
                    <Link
                      to="/global-admin"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiGlobe className="mr-3" />
                      Panel Global
                    </Link>
                    <Link
                      to="/register-business"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center"
                    >
                      <FiPlus className="mr-3" />
                      Crear Negocio
                    </Link>
                    <Link
                      to="/migration"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiDatabase className="mr-3" />
                      Migración
                    </Link>
                  </>
                ) : isBusinessAdmin ? (
                  <>
                    <Link
                      to="/admin/day"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiCalendar className="mr-3" />
                      Dashboard del Día
                    </Link>
                    <Link
                      to="/admin/appointments"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiCalendar className="mr-3" />
                      Citas
                    </Link>
                    <Link
                      to="/admin/accounting"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiDollarSign className="mr-3" />
                      Contabilidad
                    </Link>
                    <Link
                      to="/admin/users"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiUsers className="mr-3" />
                      Usuarios
                    </Link>
                    {/* Inventario eliminado del menú móvil */}
                    <Link
                      to="/admin/services"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiSettings className="mr-3" />
                      Servicios
                    </Link>
                    <Link
                      to="/admin/employees"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiUsers className="mr-3" />
                      Empleados
                    </Link>
                    <Link
                      to="/admin/cuadrante"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiCalendar className="mr-3" />
                      Cuadrante
                    </Link>
                    <Link
                      to="/admin/reports"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiFileText className="mr-3" />
                      Informes
                    </Link>
                    <Link
                      to="/admin/date-control"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiCalendar className="mr-3" />
                      Control de Fechas
                    </Link>
                    <Link
                      to="/admin/config"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiSettings className="mr-3" />
                      Configuración
                    </Link>
                    <Link
                      to="/support"
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 flex items-center"
                    >
                      <FiMessageSquare className="mr-3" />
                      Soporte
                    </Link>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </nav>
      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowProfileModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Editar perfil</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={profileDraft.displayName}
                    onChange={(e) => setProfileDraft({ ...profileDraft, displayName: e.target.value })}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={profileDraft.phone}
                    onChange={(e) => setProfileDraft({ ...profileDraft, phone: e.target.value })}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={userDetails?.email || user.email}
                    readOnly
                    disabled
                    className="form-input w-full bg-gray-100"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button className="btn btn-secondary" onClick={() => setShowProfileModal(false)}>{t('common.cancel') || 'Cancelar'}</button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    try {
                      const { updateUser } = await import('../services/firebase');
                      await updateUser(user.uid, { displayName: profileDraft.displayName, phone: profileDraft.phone });
                      setUserDetails((prev) => ({ ...(prev || {}), displayName: profileDraft.displayName, phone: profileDraft.phone }));
                      setShowProfileModal(false);
                    } catch (e) { console.warn(e); }
                  }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;

